import { Tabs, TabList, TabPanel, TabPanels, Box } from '@chakra-ui/react';
import { useState, useRef } from 'react';
import { CustomTabWithStep } from '@/components/ats/Tabs';
import { Drawer } from '@/components/ats';
import { FormBuilder } from '@/components/formBuilder';
import { checkIfAnyFieldTrue } from '@/utils/schedule-interview-helpers';
// import { debounce } from 'lodash-es';
import {
  useCreateProjectMutation,
  useGetInternalTeamProjectsQuery,
  useUpdateProjectMutation,
} from '@/store/query/projects';
import { SelectOptionWithAvatar } from '@/components/formBuilder/SelectComponents';
import { BasicInfoV1 } from './basicInfoV1';
import { useShowToast } from '@/hooks/useShowToast';

export const CreateProjectDrawer = ({
  modalActions,
  fetchProjectsInBg = () => {},
  setCreatedProjectData = (_data) => {},
}) => {
  const basicInfoRef = useRef(null);
  const projectTeamRef = useRef(null);
  const [currentTab, setCurrentTab] = useState('basicInfo');
  const [projectDetails, setProjectDetails] = useState<any>({});
  const [projectViewersPayload, setProjectViewersPayload] = useState<any[]>();
  const [mode, setMode] = useState('create');
  const [projectEditorsPayload, setProjectEditorsPayload] = useState<any[]>();
  // const [projectName, setProjectName] = useState(false);

  const [createProject, { isLoading: isCreateLoading }] =
    useCreateProjectMutation();
  const [updateProject, { isLoading: isUpdateLoading }] =
    useUpdateProjectMutation();

  const toast = useShowToast();

  const { data: usersData, isLoading: isUsersLoading } =
    useGetInternalTeamProjectsQuery(
      {
        projectId: projectDetails?.id,
      },
      { skip: !projectDetails?.id }
    );

  const ProjectTeam = (props) => {
    const getProjectViewersOptions = (usersData) => {
      const filteredProjectViewersOptions = usersData?.filter((user) => {
        return !projectEditorsPayload?.find((editor) => user?.id === editor);
      });
      return filteredProjectViewersOptions;
    };
    const getProjectEditorsOptions = (usersData) => {
      const filteredProjectEditorsOptions = usersData?.filter((user) => {
        return !projectViewersPayload?.find((viewer) => user?.id === viewer);
      });
      return filteredProjectEditorsOptions;
    };

    const ProjectTeamConfig = [
      {
        name: 'project_viewers',
        label: 'Viewer(s)',
        subLabel:
          'Viewers have access to the project but cannot make any changes to it.',
        labelInfoText:
          "Users with the 'Manage all projects' permission can fully manage projects even if they are added as viewers",
        type: 'select',
        isMulti: true,
        options: getProjectViewersOptions(usersData),
        isAppendToBody: true,
        useInput: true,
        isLoading: isUsersLoading,
        customFormatLabel: SelectOptionWithAvatar,
        valueKey: 'id',
        labelKey: 'name',
      },
      {
        name: 'project_editors',
        label: 'Editor(s)',
        type: 'select',
        isMulti: true,
        subLabel:
          'Editors can add more candidates, invite collaborators, and edit project settings. They cannot archive projects.',
        labelInfoText:
          "Users with the 'Manage all projects' permission can fully manage projects even if they are added as editors",
        options: getProjectEditorsOptions(usersData),
        validationType: 'array',
        isAppendToBody: true,
        isLoading: isUsersLoading,
        customFormatLabel: SelectOptionWithAvatar,
        valueKey: 'id',
        labelKey: 'name',
      },
    ];
    const handleViewerEditorFilterChange = (values) => {
      if (values?.project_viewers) {
        setProjectViewersPayload(values?.project_viewers);
      }
      if (values?.project_editors) {
        setProjectEditorsPayload(values?.project_editors);
      }
    };

    return (
      <Box px="sm">
        <FormBuilder
          config={ProjectTeamConfig}
          formRef={props?.formRef}
          isObserveEnabled={true}
          observFunc={handleViewerEditorFilterChange}
          initialValues={{
            project_viewers: projectViewersPayload,
            project_editors: projectEditorsPayload,
          }}
        />
      </Box>
    );
  };

  const [err, setErr] = useState({
    basicInfo: false,
    projectTeam: false,
  });

  // const getNextTab = () => {
  //   const currentIndex = projectTabs.findIndex((tab) => tab.id === currentTab);
  //   return projectTabs?.[currentIndex + 1];
  // };

  // const debouncedUpdateProjName = debounce((name) => {
  //   if (name) {
  //     setProjectName(true);
  //   } else {
  //     setProjectName(false);
  //   }
  // }, 250);

  const projectTabs = [
    {
      id: 'basicInfo',
      label: 'Basic Info',
      component: BasicInfoV1,
      props: {
        formRef: basicInfoRef,
        px: 'sm',
        py: '0px',
        projectDetails: projectDetails,
        // canCreateTheProject: (values) => {
        //   const projectName = values?.target?.value;
        //   debouncedUpdateProjName(projectName);
        // },
      },
    },
    {
      id: 'team',
      label: 'Project Team',
      component: ProjectTeam,
      props: { formRef: projectTeamRef },
    },
  ];

  const submitFormsAndGetErrors = async () => {
    await basicInfoRef?.current?.submitForm();
    if (!basicInfoRef?.current?.values.name) {
      await basicInfoRef?.current?.validate?.();
    }
    return {
      basicInfo: !basicInfoRef?.current?.isValid,
      projectTeam: false,
    };
  };

  const clickHandler = async () => {
    const createProjectPayload = () => {
      return {
        name: basicInfoRef?.current?.values?.name,
        description: basicInfoRef?.current?.values?.description,
        talent_pool_id: basicInfoRef?.current?.values?.talent_pool_id?.value,
      };
    };

    if (currentTab === 'basicInfo') {
      const errors = await submitFormsAndGetErrors();
      setErr(errors);
      if (checkIfAnyFieldTrue(errors)) {
        return;
      }
      try {
        if (!projectDetails?.id) {
          const data = await createProject(createProjectPayload()).unwrap();
          setCreatedProjectData(data);
          handleResult(data);
        } else {
          setMode('update');
          const data = await updateProject({
            projectId: projectDetails.id,
            ...createProjectPayload(),
          }).unwrap();
          handleResult(data);
        }
      } catch {
        handleResult(undefined);
      }
    }
    if (currentTab === 'team' && projectDetails?.id) {
      const { project_viewers, project_editors } =
        projectTeamRef?.current?.values || {};
      let projectViewersPayload = [];
      let projectEditorsPayload = [];
      if (Array.isArray(project_viewers) && project_viewers.length > 0) {
        const projectViewers = usersData.filter((user) =>
          project_viewers.includes(user.id)
        );

        if (projectViewers.length > 0) {
          projectViewersPayload = projectViewers.map((viewer) => ({
            access: 'project_viewer',
            invitation_message:
              "I'm inviting you to join this project. Let's collaborate to build the talent pipeline effectively",
            user_id: viewer.id,
          }));
        }
      }
      if (Array.isArray(project_editors) && project_editors.length > 0) {
        const projectEditors = usersData.filter((user) =>
          project_editors.includes(user.id)
        );

        if (project_editors.length > 0) {
          projectEditorsPayload = projectEditors.map((viewer) => ({
            access: 'project_editor',
            invitation_message:
              "I'm inviting you to join this project. Let's collaborate to build the talent pipeline effectively",
            user_id: viewer.id,
          }));
        }
      }
      const mergedPayload = projectViewersPayload?.concat(
        projectEditorsPayload
      );

      try {
        const data = await updateProject({
          projectId: projectDetails.id,
          access: 2,
          project_collaborators_attributes: mergedPayload,
          ...createProjectPayload(),
        }).unwrap();
        handleResult(data);
      } catch {
        handleResult(undefined);
      }
    }
  };

  const handleResult = (result) => {
    setProjectDetails(result);
    if (result?.id) {
      toast({
        title: `Project ${
          projectDetails?.id ? 'updated' : 'created'
        } successfully`,
        position: 'bottom-left',
        status: 'success',
        isClosable: true,
      });
      if (projectDetails?.id) {
        modalActions?.onClose();
      }
      if (mode === 'create') {
        setCurrentTab('team');
        fetchProjectsInBg();
      } else {
        modalActions?.onClose();
      }
    } else {
      toast({
        title: `Project creation failed`,
        position: 'bottom-left',
        status: 'error',
        isClosable: true,
      });
    }
  };
  const handleModalOnClose = () => {
    setProjectDetails({});
    setProjectViewersPayload([]);
    setProjectEditorsPayload([]);
    setCurrentTab('basicInfo');
    setErr({
      basicInfo: false,
      projectTeam: false,
    });
    modalActions?.onClose();
  };

  const extendedModalActions = {
    ...modalActions,
    onClose: handleModalOnClose,
  };

  return (
    <>
      <Drawer
        drawerActions={extendedModalActions}
        onDrawerSubmit={clickHandler}
        headerText="Create project"
        headerDescription="Use projects to source and build your talent pool"
        ctaLabel={currentTab === 'basicInfo' ? `Next: Project team` : 'Save'}
        hasFooterDivider={true}
        isCtaLoading={isCreateLoading || isUpdateLoading}
        chakraDrawerProps={{
          trapFocus: true,
        }}
      >
        <Tabs
          display="flex"
          flexDir="column"
          flex="1"
          h="100%"
          maxH="calc(100% - 130px)"
          alignItems="stretch"
          index={projectTabs?.findIndex((option) => option?.id === currentTab)}
          isLazy
        >
          <TabList
            borderTop="1px solid #EAEAEA"
            borderBottom={'1px solid #EAEAEA'}
            background={'ats-tertiary'}
            p="sm"
          >
            {projectTabs.map((option, idx) => {
              return (
                <CustomTabWithStep
                  key={option.id}
                  label={option?.label}
                  step={String(idx + 1)}
                  arrow={option?.id !== 'team'}
                  onClick={() => {
                    if (!projectDetails?.id) {
                      submitFormsAndGetErrors();
                    } else {
                      setCurrentTab(option?.id);
                    }
                  }}
                  showError={err?.[option?.id]}
                  defaultTabSelectedStyles
                />
              );
            })}
          </TabList>
          <TabPanels>
            {projectTabs.map((option) => {
              const Component = option.component;
              return (
                <TabPanel
                  key={option?.id}
                  maxH="100%"
                  flex="1"
                  overflowY="auto"
                >
                  <Component {...option?.props} />
                </TabPanel>
              );
            })}
          </TabPanels>
        </Tabs>
      </Drawer>
    </>
  );
};
